import * as THREE from 'three';
import { findClosedPolygons } from './floorPlanDesignerGeometry.js';
import { getWallSegmentsResolved } from './floorPlanDesignerState.js';
import { buildWallMountedOpeningItems } from './floorPlanOpening2D.js';
import {
  getBackgroundRenderedSize,
  normalizeBackgroundReferenceImage,
} from './floorPlanBackgroundImageState.js';

/**
 * @typedef {{ minX: number; maxX: number; minY: number; maxY: number; widthM?: number; heightM?: number }} Bounds2D
 */

/**
 * @param {Bounds2D | null | undefined} a
 * @param {Bounds2D | null | undefined} b
 * @returns {Bounds2D | null}
 */
export function unionBounds(a, b) {
  if (!a) return b ? { ...b } : null;
  if (!b) return { ...a };
  const minX = Math.min(a.minX, b.minX);
  const maxX = Math.max(a.maxX, b.maxX);
  const minY = Math.min(a.minY, b.minY);
  const maxY = Math.max(a.maxY, b.maxY);
  return {
    minX,
    maxX,
    minY,
    maxY,
    widthM: maxX - minX,
    heightM: maxY - minY,
  };
}

/**
 * @param {Bounds2D | null | undefined} bounds
 * @param {number} [paddingRatio]
 * @returns {Bounds2D | null}
 */
export function boundsWithPadding(bounds, paddingRatio = 0.18) {
  if (!bounds) return null;
  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  const padX = width * paddingRatio;
  const padY = height * paddingRatio;
  const minPad = 50;
  const px = Math.max(padX, minPad);
  const py = Math.max(padY, minPad);
  return {
    minX: bounds.minX - px,
    maxX: bounds.maxX + px,
    minY: bounds.minY - py,
    maxY: bounds.maxY + py,
    widthM: width + px * 2,
    heightM: height + py * 2,
  };
}

/**
 * @param {import('./floorPlanBackgroundImageState.js').BackgroundReferenceImage | null | undefined} background
 * @param {number} metersPerPixel
 * @returns {Bounds2D | null}
 */
export function getBackgroundReferenceWorldBounds(background, metersPerPixel) {
  const normalized = normalizeBackgroundReferenceImage(background, metersPerPixel);
  if (!normalized || normalized.visible === false) return null;

  const mpp = Number.isFinite(metersPerPixel) && metersPerPixel > 0 ? metersPerPixel : 0.01;
  const { widthMeters, heightMeters } = getBackgroundRenderedSize(normalized);
  if (widthMeters <= 0 || heightMeters <= 0) return null;

  const halfW = widthMeters / mpp / 2;
  const halfH = heightMeters / mpp / 2;
  const cx = normalized.worldX;
  const cy = normalized.worldY;
  const angleRad = (normalized.rotationDegrees * Math.PI) / 180;

  const corners = [
    { x: -halfW, y: -halfH },
    { x: halfW, y: -halfH },
    { x: halfW, y: halfH },
    { x: -halfW, y: halfH },
  ];

  const xs = [];
  const ys = [];
  corners.forEach(({ x, y }) => {
    const rx = x * Math.cos(angleRad) - y * Math.sin(angleRad);
    const ry = x * Math.sin(angleRad) + y * Math.cos(angleRad);
    xs.push(cx + rx);
    ys.push(cy + ry);
  });

  if (!xs.length) return null;

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    minX,
    maxX,
    minY,
    maxY,
    widthM: maxX - minX,
    heightM: maxY - minY,
  };
}

/**
 * @param {{
 *   state: import('./floorPlanDesignerState.js').DesignerState;
 *   includeBackground?: boolean;
 * }} params
 * @returns {Bounds2D | null}
 */
export function getFloorPlan2DBounds({ state, includeBackground = true }) {
  if (!state) return null;

  const xs = [];
  const ys = [];
  const mpp = Number(state?.scale?.metersPerPixel) || 0.01;

  const addPoint = (x, y, paddingPx = 0) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const pad = Number.isFinite(paddingPx) ? Math.max(0, paddingPx) : 0;
    xs.push(x - pad, x + pad);
    ys.push(y - pad, y + pad);
  };

  const addSegment = (start, end, paddingPx = 0) => {
    if (!Array.isArray(start) || !Array.isArray(end)) return;
    addPoint(start[0], start[1], paddingPx);
    addPoint(end[0], end[1], paddingPx);
  };

  (state.points || []).forEach((point) => {
    addPoint(point.x, point.y);
  });

  const segments =
    Array.isArray(state.walls) && Array.isArray(state.points)
      ? getWallSegmentsResolved(state)
      : [];

  segments.forEach((segment) => {
    const thicknessM =
      Number(segment.thicknessMeters) ||
      Number(state?.wallSettings?.thicknessMeters) ||
      0;
    addSegment(segment.start, segment.end, mpp > 0 ? thicknessM / mpp / 2 : 0);
  });

  try {
    const closedRooms = findClosedPolygons(state);
    closedRooms.forEach((room) => {
      (room.polygon || []).forEach((point) => {
        if (Array.isArray(point)) {
          addPoint(point[0], point[1]);
        }
      });
    });
  } catch {
    // ignore geometry errors on partial state
  }

  (state.objects || []).forEach((object) => {
    if (object.type !== 'furniture') return;
    const centerX = Number(object.x) / mpp;
    const centerY = Number(object.z) / mpp;
    const halfWidth = (Number(object.width) || 0) / mpp / 2;
    const halfDepth = (Number(object.depth) || 0) / mpp / 2;
    addPoint(centerX, centerY, Math.max(halfWidth, halfDepth));
  });

  const openingItems = buildWallMountedOpeningItems(state, segments, mpp);
  openingItems.forEach((item) => {
    const halfThicknessPx = item.thicknessM / mpp / 2;
    addSegment(item.p0, item.p1, halfThicknessPx);
  });

  let bounds = null;
  if (xs.length && ys.length) {
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    bounds = {
      minX: minX === maxX ? minX - 50 : minX,
      maxX: minX === maxX ? maxX + 50 : maxX,
      minY: minY === maxY ? minY - 50 : minY,
      maxY: minY === maxY ? maxY + 50 : maxY,
      widthM: minX === maxX ? 100 : maxX - minX,
      heightM: minY === maxY ? 100 : maxY - minY,
    };
  }

  if (includeBackground && state.backgroundReferenceImage) {
    const bgBounds = getBackgroundReferenceWorldBounds(
      state.backgroundReferenceImage,
      mpp,
    );
    bounds = unionBounds(bounds, bgBounds);
  }

  return bounds;
}

/**
 * @param {{
 *   roots: Array<THREE.Object3D | null | undefined>;
 *   excludeNames?: string[];
 * }} params
 * @returns {THREE.Box3}
 */
export function getFloorPlan3DBounds({ roots, excludeNames = ['grid'] }) {
  const box = new THREE.Box3();
  const exclude = new Set(excludeNames);

  roots.forEach((root) => {
    if (!root) return;
    if (root.name && exclude.has(root.name)) return;
    root.traverse((child) => {
      if (child.name && exclude.has(child.name)) return;
      if (child instanceof THREE.Mesh || child instanceof THREE.Line || child instanceof THREE.Points) {
        if (!child.visible) return;
        const childBox = new THREE.Box3().setFromObject(child);
        if (!childBox.isEmpty() && Number.isFinite(childBox.min.x)) {
          box.union(childBox);
        }
      }
    });
  });

  return box;
}

/**
 * @param {THREE.PerspectiveCamera} camera
 * @param {import('three/examples/jsm/controls/OrbitControls.js').OrbitControls} controls
 * @param {THREE.Box3} box
 * @param {{ paddingFactor?: number }} [options]
 */
export function fitCameraToBounds3D(camera, controls, box, options = {}) {
  const paddingFactor = options.paddingFactor ?? 1.25;
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();

  if (!box.isEmpty()) {
    box.getCenter(center);
    box.getSize(size);
  }

  const maxDim = box.isEmpty()
    ? NaN
    : Math.max(size.x, size.y, size.z, 0.5);

  if (box.isEmpty() || !Number.isFinite(maxDim)) {
    camera.position.set(4, 4, 4);
    camera.lookAt(0, 0, 0);
    controls.target.set(0, 0, 0);
    camera.near = 0.1;
    camera.far = 1000;
    camera.updateProjectionMatrix();
    controls.update();
    return;
  }

  const offset = camera.position.clone().sub(controls.target);
  const defaultDirection = new THREE.Vector3(0.8, 0.6, 0.8).normalize();
  const direction = offset.lengthSq() > 1e-6 ? offset.normalize() : defaultDirection;

  const fovRad = THREE.MathUtils.degToRad(camera.fov);
  const aspect = Math.max(camera.aspect || 1, 0.0001);
  const fitHeightDistance = maxDim / (2 * Math.tan(fovRad / 2));
  const fitWidthDistance = fitHeightDistance / aspect;
  const fitDistance = Math.max(fitHeightDistance, fitWidthDistance) * paddingFactor;
  const cameraDistance = Math.max(fitDistance, 2);

  controls.target.copy(center);
  camera.position.copy(center.clone().add(direction.multiplyScalar(cameraDistance)));
  camera.near = Math.max(0.01, cameraDistance / 100);
  camera.far = Math.max(1000, cameraDistance * 100);
  camera.updateProjectionMatrix();
  controls.update();
}
